import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { FaCalculator, FaQuestionCircle, FaChartBar, FaInfoCircle, FaCheckCircle } from 'react-icons/fa';
import Chart from 'react-apexcharts';

const DebtConsolidationCalculator = () => {
  const [totalDebt, setTotalDebt] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [monthlyPayment, setMonthlyPayment] = useState('');
  const [totalRepayment, setTotalRepayment] = useState('');
  const [timeToPayOff, setTimeToPayOff] = useState('');
  const [chartData, setChartData] = useState(null);

  useEffect(() => {
    // Initialize chart data
    const chartOptions = {
      series: [{
        name: 'Total Debt',
        data: [0]
      }, {
        name: 'Total Repayment',
        data: [0]
      }],
      options: {
        chart: {
          type: 'bar',
          height: 350,
          toolbar: {
            show: false
          }
        },
        plotOptions: {
          bar: {
            borderRadius: 8,
            horizontal: true,
            columnWidth: '60%',
            colors: {
              ranges: [
                {
                  from: 0,
                  to: 1,
                  color: '#4361ee'
                },
                {
                  from: 1,
                  to: 2,
                  color: '#f6416c'
                }
              ]
            }
          },
        },
        dataLabels: {
          enabled: false
        },
        xaxis: {
          categories: ['Total Debt', 'Total Repayment'],
          labels: {
            style: {
              fontSize: '14px',
              colors: '#666'
            }
          }
        },
        yaxis: {
          labels: {
            style: {
              fontSize: '14px',
              colors: '#666'
            }
          }
        },
        legend: {
          position: 'top',
          fontSize: '14px',
          fontFamily: 'Helvetica, Arial, sans-serif',
          labels: {
            colors: '#666'
          }
        },
        fill: {
          opacity: 1
        },
        tooltip: {
          y: {
            formatter: function (val) {
              return `$${val.toFixed(2)}`;
            }
          }
        }
      },
    };
    setChartData(chartOptions);
  }, []);

  const calculate = () => {
    const totalDebtAmount = parseFloat(totalDebt);
    const interestRateAmount = parseFloat(interestRate) / 100 / 12;
    const monthlyPaymentAmount = parseFloat(monthlyPayment);

    if (!totalDebtAmount || !interestRateAmount || !monthlyPaymentAmount) {
      alert('Please fill in all fields.');
      return;
    }

    // Calculate total repayment
    let remainingBalance = totalDebtAmount;
    let totalPaid = 0;
    let months = 0;

    while (remainingBalance > 0) {
      const interest = remainingBalance * interestRateAmount;
      const principal = monthlyPaymentAmount - interest;
      
      if (principal <= 0) {
        alert('Monthly payment is too low to cover interest.');
        return;
      }

      remainingBalance -= principal;
      totalPaid += monthlyPaymentAmount;
      months++;

      if (months > 1200) { // Prevent infinite loop
        break;
      }
    }

    setTotalRepayment(totalPaid.toFixed(2));
    setTimeToPayOff((months / 12).toFixed(1) + ' years');

    // Update chart data
    if (chartData) {
      const updatedData = {
        ...chartData,
        series: [{
          name: 'Total Debt',
          data: [totalDebtAmount, 0]
        }, {
          name: 'Total Repayment',
          data: [0, totalPaid]
        }],
        options: {
          ...chartData.options,
          xaxis: {
            ...chartData.options.xaxis,
            categories: ['Total Debt', 'Total Repayment'],
          },
        },
      };
      setChartData(updatedData);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Debt Consolidation Calculator | Financial Tool</title>
        <meta name="description" content="Calculate your debt consolidation plan and see how much you can save with our Debt Consolidation Calculator." />
        <meta name="keywords" content="debt consolidation calculator, financial tool, loan calculator, debt repayment" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="calculator-header mb-12">
          <div className="flex items-center gap-4">
            <div className="header-icon text-4xl">
              <FaCalculator />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Debt Consolidation Calculator</h1>
              <p className="text-lg text-blue-600 font-semibold">Calculate Your Debt Consolidation Plan</p>
            </div>
          </div>
        </div>

        <div className="calculator-description space-y-4 mb-12">
          <p className="text-gray-700">
            The Debt Consolidation Calculator helps you plan your debt repayment strategy and see how much you can save by consolidating your debts.
          </p>
        </div>

        <div className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="totalDebt">
                Total Debt Amount
              </label>
              <input
                type="number"
                id="totalDebt"
                name="totalDebt"
                value={totalDebt}
                onChange={(e) => setTotalDebt(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="$0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="interestRate">
                Interest Rate (%)
              </label>
              <input
                type="number"
                id="interestRate"
                name="interestRate"
                value={interestRate}
                onChange={(e) => setInterestRate(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="monthlyPayment">
                Monthly Payment
              </label>
              <input
                type="number"
                id="monthlyPayment"
                name="monthlyPayment"
                value={monthlyPayment}
                onChange={(e) => setMonthlyPayment(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="$0"
              />
            </div>

            <button
              onClick={calculate}
              className="w-full bg-green-600 text-white py-3 px-4 rounded-md text-sm font-semibold hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
            >
              Calculate Debt Consolidation
            </button>
          </div>

          {totalRepayment && (
            <div className="mt-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Results</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h3 className="text-xl font-semibold mb-4">Total Debt</h3>
                  <p className="text-xl font-medium">${totalDebt}</p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h3 className="text-xl font-semibold mb-4">Interest Rate</h3>
                  <p className="text-xl font-medium">{interestRate}%</p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h3 className="text-xl font-semibold mb-4">Monthly Payment</h3>
                  <p className="text-xl font-medium">${monthlyPayment}</p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h3 className="text-xl font-semibold mb-4">Total Repayment</h3>
                  <p className="text-xl font-medium">${totalRepayment}</p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h3 className="text-xl font-semibold mb-4">Time to Pay Off</h3>
                  <p className="text-xl font-medium">{timeToPayOff}</p>
                </div>
              </div>

              <div className="mt-8">
                <h3 className="text-xl font-semibold mb-4">Debt Breakdown</h3>
                <div className="h-96">
                  {chartData && (
                    <Chart
                      options={chartData.options}
                      series={chartData.series}
                      type="bar"
                      height={350}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="calculator-section mt-24">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            How to Use Debt Consolidation Calculator <FaInfoCircle className="ml-2 text-2xl" />
          </h2>
          <div className="bg-white p-8 rounded-xl shadow-md">
            <ol className="list-decimal pl-8 space-y-4">
              <li className="flex items-center">
                <FaCheckCircle className="text-green-600 mr-2" />
                <span>Enter your total debt amount (principal)</span>
              </li>
              <li className="flex items-center">
                <FaCheckCircle className="text-green-600 mr-2" />
                <span>Input the average interest rate of your debts</span>
              </li>
              <li className="flex items-center">
                <FaCheckCircle className="text-green-600 mr-2" />
                <span>Enter the monthly payment you can afford</span>
              </li>
              <li className="flex items-center">
                <FaCheckCircle className="text-green-600 mr-2" />
                <span>Click "Calculate Debt Consolidation" to see your results</span>
              </li>
            </ol>
          </div>
        </div>

        <div className="calculator-section mt-24">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            Features and Benefits <FaChartBar className="ml-2 text-2xl" />
          </h2>
          <div className="bg-white p-8 rounded-xl shadow-md">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Key Features</h3>
                <ul className="list-disc pl-8 space-y-2">
                  <li>Comprehensive debt analysis</li>
                  <li>Real-time repayment calculations</li>
                  <li>Visual debt breakdown</li>
                  <li>Interest rate impact analysis</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Benefits</h3>
                <ul className="list-disc pl-8 space-y-2">
                  <li>Optimize debt repayment strategy</li>
                  <li>Save money on interest payments</li>
                  <li>Plan debt-free timeline</li>
                  <li>Make informed financial decisions</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-24">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            Frequently Asked Questions <FaQuestionCircle className="ml-2 text-2xl" />
          </h2>
          <div className="bg-white p-8 rounded-xl shadow-md">
            <div className="space-y-8">
              <div>
                <h3 className="text-xl font-semibold text-blue-600 mb-4">What is debt consolidation?</h3>
                <p className="text-gray-700">
                  Debt consolidation is a strategy that combines multiple debts into a single loan with a lower interest rate,
                  making it easier to manage and potentially saving money on interest payments.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-blue-600 mb-4">How does this calculator help?</h3>
                <p className="text-gray-700">
                  This calculator helps you understand how consolidating your debts can affect your monthly payments,
                  total repayment amount, and time to become debt-free.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-blue-600 mb-4">What factors affect my debt consolidation plan?</h3>
                <p className="text-gray-700">
                  Your total debt amount, interest rate, and monthly payment capacity are the key factors that determine
                  how long it will take to pay off your debts and how much you'll pay in total.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Is debt consolidation right for me?</h3>
                <p className="text-gray-700">
                  Debt consolidation can be beneficial if you have multiple high-interest debts and can afford a
                  consistent monthly payment. However, it's essential to consider all factors and possibly consult
                  with a financial advisor.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DebtConsolidationCalculator;